import React, { useState, useMemo } from "react";
import PageHeader from "../components/PageHeader";
import { getApiBase } from "../api";

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

const EQUIPMENT_TYPES = [
  { value: "DRY_VAN", label: "Dry Van" },
  { value: "REEFER", label: "Reefer" },
  { value: "FLATBED", label: "Flatbed" },
  { value: "STEP_DECK", label: "Step Deck" },
  { value: "LOWBOY", label: "Lowboy" },
  { value: "TANKER", label: "Tanker" },
  { value: "INTERMODAL", label: "Intermodal" },
  { value: "BOX_TRUCK", label: "Box Truck" },
  { value: "POWER_ONLY", label: "Power Only" },
  { value: "HOTSHOT", label: "Hotshot" },
];

function money(cents) {
  if (cents === null || cents === undefined) return "—";
  return `$${(Number(cents) / 100).toFixed(2)}`;
}

function moneyPerMile(cents) {
  if (cents === null || cents === undefined) return "—";
  return `$${(Number(cents) / 100).toFixed(2)}/mi`;
}

export default function RateInsights() {
  const apiBase = useMemo(() => getApiBase(), []);
  
  const [originState, setOriginState] = useState("");
  const [destState, setDestState] = useState("");
  const [equipmentType, setEquipmentType] = useState("DRY_VAN");
  const [distanceMiles, setDistanceMiles] = useState("500");
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [laneData, setLaneData] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [marketData, setMarketData] = useState(null);

  async function searchLane() {
    if (!originState || !destState) {
      setError("Please select origin and destination states");
      return;
    }
    setLoading(true);
    setError("");
    setLaneData(null);
    setSuggestion(null);
    
    try {
      // Get lane data
      const laneRes = await fetch(`${apiBase}/rate-insights/lane?originState=${originState}&destState=${destState}&equipmentType=${equipmentType}`);
      if (laneRes.ok) {
        const data = await laneRes.json();
        setLaneData(data);
      }
      
      // Get rate suggestion
      const suggestRes = await fetch(`${apiBase}/rate-insights/suggest?originState=${originState}&destState=${destState}&equipmentType=${equipmentType}&distanceMiles=${distanceMiles}`);
      if (suggestRes.ok) {
        const data = await suggestRes.json();
        setSuggestion(data);
      }
    } catch (e) {
      setError(e.message || "Failed to fetch rate data");
    } finally {
      setLoading(false);
    }
  }

  async function loadMarketData() {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${apiBase}/rate-insights/market?equipmentType=${equipmentType}`);
      if (res.ok) {
        const data = await res.json();
        setMarketData(data);
      }
    } catch (e) {
      setError(e.message || "Failed to fetch market data");
    } finally {
      setLoading(false);
    }
  }

  const confidenceColor = (conf) => {
    if (conf === "HIGH") return "#059669";
    if (conf === "MEDIUM") return "#d97706";
    return "#dc2626";
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      <PageHeader 
        title="📊 Rate Insights" 
        subtitle="Historical lane rates and market trends"
      />

      {/* Lane Search */}
      <div style={{ marginTop: 20, padding: 20, background: "white", borderRadius: 12, border: "1px solid #e5e7eb" }}>
        <h3 style={{ margin: "0 0 16px 0", fontWeight: 700 }}>🔍 Search Lane Rates</h3>
        
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Origin State</label>
            <select
              value={originState}
              onChange={(e) => setOriginState(e.target.value)}
              style={{ padding: 10, borderRadius: 6, border: "1px solid #d1d5db", minWidth: 100 }}
            >
              <option value="">Select...</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          
          <div style={{ fontSize: 24, color: "#9ca3af" }}>→</div>
          
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Destination State</label>
            <select
              value={destState}
              onChange={(e) => setDestState(e.target.value)}
              style={{ padding: 10, borderRadius: 6, border: "1px solid #d1d5db", minWidth: 100 }}
            >
              <option value="">Select...</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Equipment</label>
            <select
              value={equipmentType}
              onChange={(e) => setEquipmentType(e.target.value)}
              style={{ padding: 10, borderRadius: 6, border: "1px solid #d1d5db", minWidth: 140 }}
            >
              {EQUIPMENT_TYPES.map(eq => <option key={eq.value} value={eq.value}>{eq.label}</option>)}
            </select>
          </div>
          
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Distance (mi)</label>
            <input
              type="number"
              value={distanceMiles}
              onChange={(e) => setDistanceMiles(e.target.value)}
              style={{ padding: 10, borderRadius: 6, border: "1px solid #d1d5db", width: 100 }}
            />
          </div>
          
          <button
            onClick={searchLane}
            disabled={loading}
            style={{
              padding: "10px 24px",
              background: "#2563eb",
              color: "white",
              border: "none",
              borderRadius: 8,
              fontWeight: 700,
              cursor: loading ? "not-allowed" : "pointer"
            }}
          >
            {loading ? "Searching..." : "Search Lane"}
          </button>
          
          <button
            onClick={loadMarketData}
            disabled={loading}
            style={{
              padding: "10px 24px",
              background: "#7c3aed",
              color: "white",
              border: "none",
              borderRadius: 8,
              fontWeight: 700,
              cursor: loading ? "not-allowed" : "pointer"
            }}
          >
            View Market Trends
          </button>
        </div>
        
        {error && (
          <div style={{ marginTop: 16, padding: 12, background: "#fee2e2", borderRadius: 8, color: "#991b1b" }}>
            {error}
          </div>
        )}
      </div>

      {/* Rate Suggestion */}
      {suggestion && (
        <div style={{ marginTop: 20, padding: 20, background: "#f0fdf4", borderRadius: 12, border: "1px solid #86efac" }}>
          <h3 style={{ margin: "0 0 16px 0", fontWeight: 700, color: "#065f46" }}>
            💡 Suggested Rate: {originState} → {destState}
          </h3>
          
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
            <div style={{ padding: 16, background: "white", borderRadius: 8, textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Suggested Rate/Mile</div>
              <div style={{ fontSize: 28, fontWeight: 900, color: "#059669" }}>
                {moneyPerMile(suggestion.suggestion?.ratePerMile)}
              </div>
            </div>
            
            <div style={{ padding: 16, background: "white", borderRadius: 8, textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Total ({distanceMiles} mi)</div>
              <div style={{ fontSize: 28, fontWeight: 900, color: "#059669" }}>
                {money(suggestion.suggestion?.totalRate)}
              </div>
            </div>
            
            <div style={{ padding: 16, background: "white", borderRadius: 8, textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Rate Range</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                {money(suggestion.suggestion?.lowRate)} - {money(suggestion.suggestion?.highRate)}
              </div>
            </div>
            
            <div style={{ padding: 16, background: "white", borderRadius: 8, textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Confidence</div>
              <div style={{ 
                fontSize: 18, 
                fontWeight: 700,
                color: confidenceColor(suggestion.suggestion?.confidence)
              }}>
                {suggestion.suggestion?.confidence || "N/A"}
              </div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                {suggestion.suggestion?.reason}
              </div>
            </div>
          </div>
          
          {suggestion.historical && (
            <div style={{ marginTop: 16, padding: 12, background: "white", borderRadius: 8 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Historical Data (Last 30 Days)</div>
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 14 }}>
                <div>Avg: <b>{moneyPerMile(suggestion.historical.avgRatePerMile)}</b></div>
                <div>Min: <b>{moneyPerMile(suggestion.historical.minRatePerMile)}</b></div>
                <div>Max: <b>{moneyPerMile(suggestion.historical.maxRatePerMile)}</b></div>
                <div>Median: <b>{moneyPerMile(suggestion.historical.medianRatePerMile)}</b></div>
                <div>Sample Size: <b>{suggestion.historical.sampleCount} loads</b></div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Lane Weekly Trend */}
      {laneData?.weeklyTrend?.length > 0 && (
        <div style={{ marginTop: 20, padding: 20, background: "white", borderRadius: 12, border: "1px solid #e5e7eb" }}>
          <h3 style={{ margin: "0 0 16px 0", fontWeight: 700 }}>📈 Weekly Trend: {originState} → {destState}</h3>
          
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  <th style={{ padding: 12, textAlign: "left", borderBottom: "2px solid #e5e7eb" }}>Week</th>
                  <th style={{ padding: 12, textAlign: "left", borderBottom: "2px solid #e5e7eb" }}>Equipment</th>
                  <th style={{ padding: 12, textAlign: "right", borderBottom: "2px solid #e5e7eb" }}>Avg Rate/Mile</th>
                  <th style={{ padding: 12, textAlign: "right", borderBottom: "2px solid #e5e7eb" }}>Sample Size</th>
                </tr>
              </thead>
              <tbody>
                {laneData.weeklyTrend.map((w, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #e5e7eb" }}>
                    <td style={{ padding: 12 }}>{new Date(w.week).toLocaleDateString()}</td>
                    <td style={{ padding: 12 }}>{w.equipmentType}</td>
                    <td style={{ padding: 12, textAlign: "right", fontWeight: 700 }}>{moneyPerMile(w.avgRatePerMile)}</td>
                    <td style={{ padding: 12, textAlign: "right" }}>{w.sampleCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Market Overview */}
      {marketData && (
        <div style={{ marginTop: 20 }}>
          <div style={{ padding: 20, background: "white", borderRadius: 12, border: "1px solid #e5e7eb" }}>
            <h3 style={{ margin: "0 0 16px 0", fontWeight: 700 }}>🌎 Market Overview (Last 30 Days)</h3>
            
            {/* Rates by Equipment */}
            {marketData.ratesByEquipment?.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <h4 style={{ margin: "0 0 12px 0", fontWeight: 600 }}>Average Rates by Equipment Type</h4>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
                  {marketData.ratesByEquipment.map((eq, i) => (
                    <div key={i} style={{ padding: 16, background: "#f9fafb", borderRadius: 8 }}>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>{eq.equipmentType}</div>
                      <div style={{ fontSize: 20, fontWeight: 900, color: "#1f2937" }}>
                        {moneyPerMile(eq.avgRatePerMile)}
                      </div>
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>
                        {eq.sampleCount} loads
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Top Lanes */}
            {marketData.topLanes?.length > 0 && (
              <div>
                <h4 style={{ margin: "0 0 12px 0", fontWeight: 600 }}>Top Lanes by Volume</h4>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                    <thead>
                      <tr style={{ background: "#f9fafb" }}>
                        <th style={{ padding: 10, textAlign: "left", borderBottom: "2px solid #e5e7eb" }}>Lane</th>
                        <th style={{ padding: 10, textAlign: "left", borderBottom: "2px solid #e5e7eb" }}>Equipment</th>
                        <th style={{ padding: 10, textAlign: "right", borderBottom: "2px solid #e5e7eb" }}>Avg Rate/Mi</th>
                        <th style={{ padding: 10, textAlign: "right", borderBottom: "2px solid #e5e7eb" }}>Loads</th>
                      </tr>
                    </thead>
                    <tbody>
                      {marketData.topLanes.slice(0, 10).map((lane, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid #e5e7eb" }}>
                          <td style={{ padding: 10, fontWeight: 600 }}>{lane.originState} → {lane.destState}</td>
                          <td style={{ padding: 10 }}>{lane.equipmentType}</td>
                          <td style={{ padding: 10, textAlign: "right", fontWeight: 700 }}>{moneyPerMile(lane.avgRatePerMile)}</td>
                          <td style={{ padding: 10, textAlign: "right" }}>{lane.loadCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* No Data Message */}
      {!loading && !suggestion && !marketData && (
        <div style={{ marginTop: 40, textAlign: "center", color: "#6b7280" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>Search for lane rates or view market trends</div>
          <div style={{ fontSize: 14, marginTop: 8 }}>
            Rate data is collected from completed loads in the system
          </div>
        </div>
      )}
    </div>
  );
}
